import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Tag, Button, Space, Typography, Spin, message, Modal, Form, Input, Select, Avatar, Alert } from 'antd';
import { EditOutlined, SaveOutlined, WalletOutlined, LinkOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';
import { COUNTRIES } from '../../../utils/countries';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
`;

const CompanyProfile: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [companyData, setCompanyData] = useState<any>(null);
  const [custodialModalVisible, setCustodialModalVisible] = useState(false);
  const [webhookModalVisible, setWebhookModalVisible] = useState(false);
  const [custodialForm] = Form.useForm();
  const [webhookForm] = Form.useForm();
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [originalCustodialValues, setOriginalCustodialValues] = useState<{
    custodialEVMAddress?: string;
    bitcoinCustodialWallet?: string;
    solanaCustodialWallet?: string;
    tronCustodialWallet?: string;
  }>({});

  useEffect(() => {
    fetchCompanyProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_COMPANY_PROFILE);
      
      if (response.data?.status === 'success' && response.data?.data?.profile) {
        const profile = response.data.data.profile;
        setCompanyData(profile);
        form.setFieldsValue({
          name: profile.name,
          tagline: profile.tagline,
          companyEmail: profile.companyEmail,
          phoneNumber: profile.phoneNumber || '',
          countryCode: profile.countryCode,
          websiteUrl: profile.websiteUrl,
          logoUrl: profile.logoUrl,
        });
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await apiClient.put(API_ENDPOINTS.MERCHANT_UPDATE_COMPANY_PROFILE, {
        email: values.companyEmail,
        phoneNumber: values.phoneNumber || null,
        countryCode: values.countryCode,
        tagline: values.tagline,
        websiteUrl: values.websiteUrl,
        logoUrl: values.logoUrl,
        membersWalletConnectName: companyData.membersWalletConnectName || '',
        webHookPostUrl: companyData.webHookPostUrl || '',
        webHookSecret: companyData.webHookSecret || '',
      });
      message.success('Company profile updated successfully');
      setEditing(false);
      fetchCompanyProfile();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update company profile');
    }
  };

  const handleCustodialSave = async () => {
    try {
      const values = await custodialForm.validateFields();
      const payload: any = {};
      
      // Normalize values for comparison (treat empty string, null, undefined as empty)
      const normalizeValue = (val: string | undefined | null) => val || '';
      
      // Only include fields that have changed
      const newEVM = normalizeValue(values.custodialEVMAddress);
      const oldEVM = normalizeValue(originalCustodialValues.custodialEVMAddress);
      if (newEVM !== oldEVM) {
        payload.custodialEVMAddress = newEVM;
      }
      
      const newBTC = normalizeValue(values.bitcoinCustodialWallet);
      const oldBTC = normalizeValue(originalCustodialValues.bitcoinCustodialWallet);
      if (newBTC !== oldBTC) {
        payload.bitcoinCustodialWallet = newBTC;
      }
      
      const newSOL = normalizeValue(values.solanaCustodialWallet);
      const oldSOL = normalizeValue(originalCustodialValues.solanaCustodialWallet);
      if (newSOL !== oldSOL) {
        payload.solanaCustodialWallet = newSOL;
      }
      
      const newTRON = normalizeValue(values.tronCustodialWallet);
      const oldTRON = normalizeValue(originalCustodialValues.tronCustodialWallet);
      if (newTRON !== oldTRON) {
        payload.tronCustodialWallet = newTRON;
      }
      
      await apiClient.put(API_ENDPOINTS.MERCHANT_UPDATE_COMPANY_PROFILE, payload);
      message.success('Custodial wallet addresses updated successfully');
      setCustodialModalVisible(false);
      fetchCompanyProfile();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update custodial addresses');
    }
  };

  const handleWebhookSave = async () => {
    try {
      const values = await webhookForm.validateFields();
      await apiClient.put(API_ENDPOINTS.MERCHANT_UPDATE_COMPANY_PROFILE, {
        email: companyData.companyEmail,
        phoneNumber: companyData.phoneNumber || null,
        countryCode: companyData.countryCode,
        tagline: companyData.tagline,
        websiteUrl: companyData.websiteUrl,
        logoUrl: companyData.logoUrl,
        membersWalletConnectName: companyData.membersWalletConnectName || '',
        webHookPostUrl: values.webHookPostUrl,
        webHookSecret: values.webHookSecret,
      });
      message.success('Webhook settings updated successfully');
      setWebhookModalVisible(false);
      fetchCompanyProfile();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update webhook settings');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!companyData) {
    return (
      <PageContainer>
        <Card>
          <Typography.Text>Company profile not found</Typography.Text>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Company Profile
        </Title>
        <Space>
          {editing ? (
            <>
              <Button onClick={() => { setEditing(false); form.resetFields(); }}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>
                Save Changes
              </Button>
            </>
          ) : (
            <Button type="primary" icon={<EditOutlined />} onClick={() => setEditing(true)}>
              Edit Profile
            </Button>
          )}
        </Space>
      </HeaderContainer>

      <StyledCard>
        {editing ? (
          <Form form={form} layout="vertical">
            <Form.Item name="name" label="Company Name">
              <Input disabled />
            </Form.Item>
            <Form.Item name="tagline" label="Tagline">
              <Input placeholder="Enter company tagline" />
            </Form.Item>
            <Form.Item name="companyEmail" label="Company Email" rules={[{ required: true, type: 'email' }]}>
              <Input placeholder="company@example.com" />
            </Form.Item>
            <Form.Item name="phoneNumber" label="Phone Number">
              <Input placeholder="Enter phone number (optional)" />
            </Form.Item>
            <Form.Item name="countryCode" label="Country" rules={[{ required: true }]}>
              <Select showSearch placeholder="Select country">
                {COUNTRIES.map((country) => (
                  <Select.Option key={country.code} value={country.code}>
                    <Space>
                      <ReactCountryFlag countryCode={country.code} svg style={{ width: '1.5em', height: '1.5em' }} />
                      {country.name}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="websiteUrl" label="Website URL">
              <Input placeholder="https://example.com" />
            </Form.Item>
            <Form.Item name="logoUrl" label="Logo URL">
              <Input placeholder="https://example.com/logo.png" />
            </Form.Item>
          </Form>
        ) : (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Company Name">{companyData.name}</Descriptions.Item>
            <Descriptions.Item label="Tagline">{companyData.tagline || '-'}</Descriptions.Item>
            <Descriptions.Item label="Company Email">{companyData.companyEmail}</Descriptions.Item>
            <Descriptions.Item label="Phone Number">{companyData.phoneNumber || '-'}</Descriptions.Item>
            <Descriptions.Item label="Country">
              {companyData.countryCode && (
                <Space>
                  <ReactCountryFlag countryCode={companyData.countryCode} svg style={{ width: '1.5em', height: '1.5em' }} />
                  {companyData.countryCode}
                </Space>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Website">
              {companyData.websiteUrl ? (
                <a href={companyData.websiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                  {companyData.websiteUrl}
                </a>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Logo">
              {companyData.logoUrl ? (
                <Avatar src={companyData.logoUrl} size={64} shape="square" />
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={companyData.status === 'ACTIVE' ? 'green' : 'default'}>
                {companyData.status}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
      </StyledCard>

      <StyledCard
        title="Custodial Wallets"
        extra={
          <Button
            type="link"
            icon={<WalletOutlined />}
            onClick={() => {
              const originalValues = {
                custodialEVMAddress: companyData.custodialEVMAddress || '',
                bitcoinCustodialWallet: companyData.bitcoinCustodialWallet || '',
                solanaCustodialWallet: companyData.solanaCustodialWallet || '',
                tronCustodialWallet: companyData.tronCustodialWallet || '',
              };
              setOriginalCustodialValues(originalValues);
              custodialForm.setFieldsValue(originalValues);
              setCustodialModalVisible(true);
            }}
          >
            {(companyData.custodialEVMAddress ||
              companyData.bitcoinCustodialWallet ||
              companyData.solanaCustodialWallet ||
              companyData.tronCustodialWallet)
              ? 'Update Addresses'
              : 'Add Addresses'}
          </Button>
        }
      >
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Custodial EVM Address">
            {companyData.custodialEVMAddress ? (
              <Typography.Text copyable style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {companyData.custodialEVMAddress}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">No custodial EVM address configured</Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Bitcoin Custodial Wallet">
            {companyData.bitcoinCustodialWallet ? (
              <Typography.Text copyable style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {companyData.bitcoinCustodialWallet}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">No Bitcoin custodial wallet configured</Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Solana Custodial Wallet">
            {companyData.solanaCustodialWallet ? (
              <Typography.Text copyable style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {companyData.solanaCustodialWallet}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">No Solana custodial wallet configured</Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Tron Custodial Wallet">
            {companyData.tronCustodialWallet ? (
              <Typography.Text copyable style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {companyData.tronCustodialWallet}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">No Tron custodial wallet configured</Typography.Text>
            )}
          </Descriptions.Item>
        </Descriptions>
        
        {/* Recommended Wallet Providers */}
        <div style={{ 
          marginTop: 20, 
          padding: 16, 
          background: 'rgba(34, 211, 238, 0.05)', 
          borderRadius: 8,
          border: '1px solid rgba(34, 211, 238, 0.2)'
        }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8, color: COLORS.textPrimary }}>
            Recommended Custodial Wallet Providers
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
            Get secure custodial wallets to store your funds from these trusted providers:
          </Typography.Text>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <a 
              href="https://mpcvault.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                background: COLORS.bgPrimary,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8,
                color: COLORS.accent,
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              <WalletOutlined />
              MPC Vault
            </a>
            <a 
              href="https://web3.okx.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                background: COLORS.bgPrimary,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8,
                color: COLORS.accent,
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              <WalletOutlined />
              OKX Web3 Wallet
            </a>
          </div>
        </div>
      </StyledCard>

      <StyledCard
        title="Webhook Settings"
        extra={
          <Button
            type="link"
            icon={<LinkOutlined />}
            onClick={() => {
              webhookForm.setFieldsValue({
                webHookPostUrl: companyData.webHookPostUrl || '',
                webHookSecret: companyData.webHookSecret || '',
              });
              setWebhookModalVisible(true);
            }}
          >
            Configure Webhook
          </Button>
        }
      >
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Webhook URL">
            {companyData.webHookPostUrl ? (
              <Typography.Text copyable style={{ wordBreak: 'break-all' }}>
                {companyData.webHookPostUrl}
              </Typography.Text>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Webhook Secret">
            {companyData.webHookSecret ? (
              <Typography.Text copyable style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {companyData.webHookSecret}
              </Typography.Text>
            ) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      <Modal
        title="Update Custodial Wallet Addresses"
        open={custodialModalVisible}
        onOk={handleCustodialSave}
        onCancel={() => setCustodialModalVisible(false)}
        okText="Save"
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Alert
          message="Admin Approval Required"
          description="This request requires admin approval before the custodial wallet addresses are updated. Changing custodial wallet addresses is not usually easy, make sure you use the right ones; this action may not be easily reversible."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
        <Form form={custodialForm} layout="vertical">
          <Form.Item
            name="custodialEVMAddress"
            label="Custodial EVM Address"
            rules={[{ required: true, message: 'Please enter the custodial EVM address' }]}
          >
            <Input placeholder="0x..." style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item
            name="bitcoinCustodialWallet"
            label="Bitcoin Custodial Wallet"
          >
            <Input placeholder="Bitcoin address" style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item
            name="solanaCustodialWallet"
            label="Solana Custodial Wallet"
          >
            <Input placeholder="Solana address" style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item
            name="tronCustodialWallet"
            label="Tron Custodial Wallet"
          >
            <Input placeholder="T..." style={{ fontFamily: 'monospace' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Configure Webhook"
        open={webhookModalVisible}
        onOk={handleWebhookSave}
        onCancel={() => setWebhookModalVisible(false)}
        okText="Save"
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form form={webhookForm} layout="vertical">
          <Form.Item
            name="webHookPostUrl"
            label="Webhook URL"
            rules={[{ required: true, type: 'url', message: 'Please enter a valid webhook URL' }]}
          >
            <Input placeholder="https://example.com/webhook" />
          </Form.Item>
          <Form.Item
            name="webHookSecret"
            label="Webhook Secret"
            rules={[{ required: true, message: 'Please enter the webhook secret' }]}
          >
            <Input.Password
              placeholder="Enter webhook secret"
              style={{ fontFamily: 'monospace' }}
              visibilityToggle={{
                visible: showWebhookSecret,
                onVisibleChange: setShowWebhookSecret,
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default CompanyProfile;

